import type { RouterInputs, RouterOutputs } from 'shared/api'
import { Button } from 'shared/components/ui/button'
import { useTranslate } from 'shared/lib/i18n'
import { cn } from 'shared/lib/utils'
import { TONE_CLASS } from 'shared/components/tone-banner'

/**
 * The Cash bag on the driver's phone (ADR-0036, ADR-0047, spec §13; tickets 31, 57):
 * at a Point of the Trip the operator packs the whole Cashbox in front of the
 * driver and writes the Point and the date on it. One bag waits per Point, and it
 * has nothing to scan: the driver takes it with «Забрать сумку пункта N», without
 * counting. Each stop's checklist names the bag beside what else is there: waiting
 * at the Point, or taken.
 */

export type DriverCashBagStops = RouterOutputs['cashBags']['ForTrip']
export type DriverCashBag = DriverCashBagStops[number]['bags'][number]
export type DriverTakeRequest = RouterInputs['cashBags']['Take']
export type DriverTakeAnswer = RouterOutputs['cashBags']['Take']

function formatMoney(amount: number, currency: string): string {
	return `${amount.toLocaleString('ru-RU')} ${currency}`
}

/** One stop's Cash bags, as rows of its checklist, the waiting one with «Забрать». Nothing when there are none. */
export function StopCashBags({
	pointCode,
	bags,
	busy = false,
	onTake,
}: {
	pointCode: string
	bags: DriverCashBag[]
	busy?: boolean
	onTake?: (cashBagId: string) => void
}) {
	const { t } = useTranslate()
	if (bags.length === 0) return null
	return (
		<div className='mt-2 flex flex-col gap-1.5'>
			<div className='text-sm font-medium'>{t('cashBag.driverTitle')}</div>
			{bags.map((bag) => (
				<div
					key={bag.cashBagId}
					className='flex flex-wrap items-center gap-2 rounded-md border px-3 py-2'
					data-testid={`driver-cash-bag-${bag.cashBagId}`}
					data-stage={bag.stage}
				>
					<span className='text-sm text-muted-foreground'>
						{t('cashBag.driverRow', {
							amount: formatMoney(bag.countedCash, bag.currency),
						})}
					</span>
					<span className='ml-auto rounded px-1.5 text-xs font-semibold ring-1 ring-current'>
						{t(`cashBagStage.${bag.stage}`)}
					</span>
					{bag.onTrip === 'waiting' && bag.stage === 'at_point' && onTake && (
						<Button
							size='sm'
							className='w-full'
							data-testid={`driver-cash-take-${bag.cashBagId}`}
							disabled={busy}
							onClick={() => onTake(bag.cashBagId)}
						>
							{t('cashBag.take', { point: pointCode })}
						</Button>
					)}
				</div>
			))}
		</div>
	)
}

/** The answer to a scan that took — or did not take — a Cash bag, in its colour. */
export function CashBagTakeBanner({ answer }: { answer: DriverTakeAnswer }) {
	const { t } = useTranslate()
	let title: string
	let detail: string
	switch (answer.outcome) {
		case 'taken':
			title = t('cashBag.answerTaken')
			detail = t('cashBag.answerTakenDetail', {
				amount: formatMoney(answer.bag.countedCash, answer.bag.currency),
				point: answer.bag.pointCode,
			})
			break
		case 'already_taken':
			title = t('cashBag.answerAlready')
			detail = t('cashBag.answerAlreadyDetail', { point: answer.bag.pointCode })
			break
		case 'refused': {
			const reason = t(`cashBagRefusal.${answer.reason}`)
			title = t('cashBag.answerRefused')
			detail = answer.pointCode
				? t('cashBag.answerRefusedPoint', { reason, point: answer.pointCode })
				: reason
			break
		}
	}
	return (
		<div
			role='status'
			className={cn('flex flex-col gap-2 rounded-lg border-2 p-4', TONE_CLASS[answer.tone])}
			data-testid='driver-cash-answer'
			data-tone={answer.tone}
			data-outcome={answer.outcome}
		>
			<span className='w-fit rounded px-1.5 text-xs font-semibold uppercase ring-1 ring-current'>
				{t(`hubScanTone.${answer.tone}`)}
			</span>
			<div className='text-3xl font-extrabold leading-tight' data-testid='driver-cash-answer-title'>
				{title}
			</div>
			<div className='text-base' data-testid='driver-cash-answer-detail'>
				{detail}
			</div>
		</div>
	)
}
